// components/CardPromoSena.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  TableFooter,
  LinearProgress
} from '@mui/material';
import {
  LocalOffer as PromoIcon,
  AttachMoney as SenaIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import moment from 'moment';
import CardExpandible from '../CardExpandible';
import { getResumenPromoSenaSucursal } from '../../services/productos.service';

const CardPromoSena = ({ 
  sucursal, 
  desde, 
  hasta, 
  loading = false,
  triggerRecarga = 0
}) => {
  // Estados
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState({});

  // Extraer datos de sucursal
  const sucursalId = sucursal?.id;
  const sucursalNombre = sucursal?.nombre;

  // Efecto para cargar datos
  useEffect(() => {
    if (sucursalId && desde && hasta) {
      cargarDatos();
    }
  }, [sucursalId, desde, hasta, triggerRecarga]);

  const cargarDatos = async () => {
    setLoadingData(true);
    setError(null);
    
    try {
      const response = await getResumenPromoSenaSucursal(sucursalId, desde, hasta);
      
      if (Array.isArray(response)) {
        setData(response);
        setPage(0);
      } else if (response.success) {
        setData(response.data || []);
        setPage(0);
      } else {
        setError(response.error || 'Error al cargar los datos');
      }
    } catch (error) {
      console.error('Error al cargar promo/seña:', error);
      setError(error.message || 'Error al cargar los datos de promo/seña');
    } finally {
      setLoadingData(false);
    }
  };

  // Manejadores de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Calcular totales
  const totalCantidad = data.reduce((sum, item) => sum + (parseInt(item.total_cantidad) || 0), 0);
  const totalProductos = data.length;

  // Obtener el porcentaje para la barra de progreso
  const getPorcentaje = (cantidad) => {
    if (totalCantidad === 0) return 0;
    return (cantidad / totalCantidad) * 100;
  };

  // Obtener color según cantidad
  const getColorByCantidad = (cantidad) => {
    const porcentaje = getPorcentaje(cantidad);
    if (porcentaje > 30) return '#2e7d32';
    if (porcentaje > 15) return '#1976d2';
    if (porcentaje > 5) return '#ed6c02';
    return '#9e9e9e';
  };

  // Paginación
  const datosPaginados = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  // Loading
  if (loading || loadingData) {
    return (
      <CardExpandible title="Promociones y Señas" icon={PromoIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#757575' }} />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            Cargando promociones y señas...
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  // Validaciones
  if (!sucursalId || !desde || !hasta) {
    let mensaje = '';
    if (!sucursalId) mensaje = 'Selecciona una sucursal';
    else if (!desde || !hasta) mensaje = 'Selecciona un rango de fechas';
    
    return (
      <CardExpandible title="Promociones y Señas" icon={PromoIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {mensaje}
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  if (error) {
    return (
      <CardExpandible title="Promociones y Señas" icon={PromoIcon}>
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </CardExpandible>
    );
  }

  if (!data || data.length === 0) {
    return (
      <CardExpandible title="Promociones y Señas" icon={PromoIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay promociones o señas en este período
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  return (
    <CardExpandible 
      title="Promociones y Señas" 
      icon={PromoIcon}
      subtitle={sucursalNombre ? `Sucursal: ${sucursalNombre} · ` : ''}
    >
      <Box>
        {/* Estadísticas */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ 
              textAlign: 'center',
              bgcolor: '#f5f5f5',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#1a1a1a', fontSize: '1.1rem' }}>
                {totalProductos}
              </Typography>
              <Typography variant="caption" color="textSecondary">Productos con promo/seña</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ 
              textAlign: 'center',
              bgcolor: '#e8f5e9',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32', fontSize: '1.1rem' }}>
                {totalCantidad}
              </Typography>
              <Typography variant="caption" color="textSecondary">Total de unidades</Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Tabla */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Producto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Cantidad</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No hay registros para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginados.map((item, index) => {
                  const globalIndex = page * rowsPerPage + index;
                  const key = `promo-${globalIndex}`;
                  const cantidad = parseInt(item.total_cantidad) || 0;
                  const porcentaje = getPorcentaje(cantidad);
                  
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <InventoryIcon fontSize="small" sx={{ color: '#757575', fontSize: 14 }} />
                            {item.producto || item.nombre || '-'}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500, color: getColorByCantidad(cantidad) }}>
                          {cantidad}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalle">
                            <IconButton 
                              size="small" 
                              onClick={() => toggleExpandRow(key)}
                              sx={{ color: '#757575' }}
                            >
                              {expandedRows[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {/* Fila expandida */}
                      <TableRow>
                        <TableCell colSpan={3} sx={{ py: 0 }}>
                          <Box sx={{ 
                            display: expandedRows[key] ? 'block' : 'none'
                          }}>
                            <Box sx={{ 
                              p: 1.5, 
                              bgcolor: '#fafafa',
                              borderTop: '1px solid #e8e8e8'
                            }}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: 0.5
                                  }}>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Código:</span>
                                      <span style={{ fontFamily: 'monospace' }}>{item.codigo || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Producto:</span>
                                      <span>{item.producto || item.nombre || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Cantidad Total:</span>
                                      <span style={{ fontWeight: 500, color: getColorByCantidad(cantidad) }}>{cantidad}</span>
                                    </Typography>
                                    {/* <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>% del Total:</span>
                                      <span>{porcentaje.toFixed(1)}%</span>
                                    </Typography> */}
                                    {/* Mostrar tipo si existe */}
                                    {item.tipo && (
                                      <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                        <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Tipo:</span>
                                        <span>
                                          <Chip 
                                            label={item.tipo}
                                            size="small"
                                            icon={item.tipo === 'promo' ? <PromoIcon fontSize="small" /> : <SenaIcon fontSize="small" />}
                                            sx={{ fontSize: '0.65rem', height: 22 }}
                                          />
                                        </span>
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={3}
                  count={data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} de ${count}`
                  }
                  sx={{
                    '.MuiTablePagination-select': {
                      fontSize: '0.8rem'
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* Información adicional */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            Sucursal ID: {sucursalId}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total productos: {data.length} · Total unidades: {totalCantidad} · Actualizado: {moment().format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
      </Box>
    </CardExpandible>
  );
};

export default CardPromoSena;